import { useState, useCallback, memo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { formatUrl, validateUrl } from "@/lib/utils";

const LinksConfigForm = memo(function LinksConfigForm({ config, onUpdate }) {
  const [links, setLinks] = useState(config.links || []);
  const [errors, setErrors] = useState({});

  // Add handler to stop propagation
  const stopPropagation = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
    }
  }, []);

  const handleChange = useCallback(
    (index, value) => {
      const newLinks = [...links];
      newLinks[index] = { ...newLinks[index], url: value };
      setLinks(newLinks);

      // Clear error when typing
      if (errors[index]) {
        const newErrors = { ...errors };
        delete newErrors[index];
        setErrors(newErrors);
      }
    },
    [links, errors]
  );

  const removeLink = useCallback(
    (index) => {
      const newLinks = [...links];
      newLinks.splice(index, 1);
      setLinks(newLinks);
      onUpdate({ links: newLinks });
    },
    [links, onUpdate]
  );

  const addLink = useCallback(
    (e) => {
      e.stopPropagation();
      const newLinks = [
        ...links,
        { id: Math.random().toString(36).substr(2, 9), url: "" },
      ];
      setLinks(newLinks);
    },
    [links]
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Validate all links
      const newErrors = {};
      let hasError = false;

      links.forEach((link, index) => {
        if (!link.url) {
          newErrors[index] = "URL is required";
          hasError = true;
        } else if (!validateUrl(link.url)) {
          newErrors[index] = "Invalid URL";
          hasError = true;
        }
      });

      setErrors(newErrors);

      if (!hasError) {
        // Format URLs and update
        const formattedLinks = links.map((link) => ({
          ...link,
          url: formatUrl(link.url),
        }));
        onUpdate({ links: formattedLinks });
      }
    },
    [links, onUpdate]
  );

  return (
    <div
      className="space-y-4"
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {links.map((link, index) => (
          <div key={link.id || index} className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-white">Link {index + 1}</Label>
              <button
                type="button"
                className="text-red-400 hover:text-red-300 p-1"
                onClick={(e) => {
                  e.stopPropagation();
                  removeLink(index);
                }}
              >
                <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
              </button>
            </div>
            <Input
              value={link.url}
              onChange={(e) => {
                stopPropagation(e);
                handleChange(index, e.target.value);
              }}
              placeholder="https://example.com"
              className="bg-white/5 border-white/10 text-white"
            />
            {errors[index] && (
              <p className="text-sm text-red-500 mt-1">{errors[index]}</p>
            )}
          </div>
        ))}

        <div className="flex justify-between pt-2">
          <button
            type="button"
            className="text-primary flex items-center gap-1 hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              addLink(e);
            }}
          >
            <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
            <span>Add Link</span>
          </button>

          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors"
            onClick={stopPropagation}
          >
            Save Links
          </button>
        </div>
      </form>
    </div>
  );
});

LinksConfigForm.displayName = "LinksConfigForm";

export default LinksConfigForm;
